
import { PackageViewMode } from '@/common/enums/PackageViewMode.enum';
import { Options, Vue } from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import { orderModule } from '@/modules/order/orderModule.vuex-module';
import { clientModule } from '@/modules/client/client.vuex-module';
import { ProvideReactive } from 'vue-property-decorator';

import AdditionalInfoBox from '@/modules/order/details/components/info-boxes/additional-info-box.component.vue';
import ItemInfoBox from '@/modules/order/details/components/info-boxes/item-info-box.component.vue';
import ContactInfoBox from '@/modules/order/details/components/info-boxes/contact-info-box.component.vue';
import OrderSummaryInfoBox from '@/modules/order/details/components/summary/order-summary.component.vue';
import PrePaymentInfoBox from '@/modules/order/details/components/info-boxes/pre-payment-info-box.component.vue';

import AlertModal from '@/common/components/alert-modal.component.vue';

import PromoCode from '../components/promo-code.component.vue';

import { OrderPayment } from '@/common/models/OrderPayment.model';
import ActiveOrder from '@/modules/order/common/classes/ActiveOrder.class';
import LoaderDisplay from '@/common/components/loader-display.component.vue';
import { PaymentMethod } from '@/common/enums/PaymentMethod.enum';
import { PaymentType } from '@/common/enums/PaymentType.enum';
import { apiService } from '@/services/api.service';

enum BoxType {
	CONTACT = 'contact',
	ADDINFO = 'addInfo',
	ITEMINFO = 'iteminfo',
	PAYMENTS = 'payments',
	LOADING = 'loading',
	NONE = 'none',
}

type PaymentResult = {
	success: boolean;
	squareJSON: string | undefined;
	squarePayment: string | undefined;
	amount: number | undefined;
	error: string | undefined;
};

@Options({
	name: 'CheckoutView',
	components: { ContactInfoBox, AdditionalInfoBox, PrePaymentInfoBox, OrderSummaryInfoBox, PromoCode, LoaderDisplay, AlertModal, ItemInfoBox },
})
export default class CheckoutView extends Vue {
	@Prop({ default: '0' }) clientLocationKey!: string;
	@Prop({ default: '0' }) type!: PackageViewMode;
	@Prop({ default: '0' }) typekey!: string;
	BoxType = BoxType;
	activeBox: BoxType = BoxType.CONTACT;
	paymentJSON: string | null = null;
	private localError: string | null = null;
	created() {
		orderModule.saveAll().then(() => {
			console.log('Setting edit item');
			//open first item after the order was saved
			let orderItem = this.activeOrder?.items.filter((i) => i.IsNew)[0];
			if (orderItem) {
				orderModule.editItem(orderItem);
			} else {
				console.log('Order does not have any new items');
			}
			this.activeBox = this.validContact ? (this.validAdditional ? BoxType.PAYMENTS : BoxType.ADDINFO) : BoxType.CONTACT;
		});
	}
	get customFieldItems() {
		return this.order.customFieldItems ?? [];
	}
	get validContact() {
		return orderModule._activeOrder?.contactInfoValid ?? false;
	}
	get validAdditional() {
		return orderModule._activeOrder?.additionalInfoValid ?? false;
	}
	get validItemInfo() {
		return orderModule._activeOrder?.customFieldsValid;
	}
	get order() {
		return orderModule;
	}
	get isLoading() {
		return orderModule.isLoading || !this.activeOrder;
	}
	get error(): string | null {
		return this.activeOrder?.error ?? this.localError;
	}
	@ProvideReactive() get orderPayment(): OrderPayment | null {
		return this.activeOrder?.paymentDue ?? null;
	}
	@ProvideReactive() get activeOrder(): ActiveOrder | null {
		return orderModule._activeOrder ?? null;
	}
	@ProvideReactive() get desktopView(): boolean {
		return clientModule.desktopView;
	}
	@Watch('activeOrder')
	onActiveOrderChange(newOrder: ActiveOrder | null, oldOrder: ActiveOrder | null): void {
		if (oldOrder && !newOrder) {
			this.$router.push(`/portal/${this.clientLocationKey}`);
		}
	}
	handleNext(): void {
		switch (this.activeBox) {
			case BoxType.CONTACT:
				this.activeBox = this.customFieldItems.length ? BoxType.ITEMINFO : BoxType.ADDINFO;
				break;
			case BoxType.ITEMINFO:
				this.activeBox = BoxType.ADDINFO;
				break;
			case BoxType.ADDINFO:
				this.activeBox = BoxType.PAYMENTS;
				break;
		}
	}
	handleEdit(box: BoxType): void {
		this.activeBox = box;
	}
	get canModifyCart(): boolean {
		const ao = this.activeOrder;
		if (!ao) return false;
		if (ao.hasPaidPayment) return false;
		return (ao.items?.length ?? 0) > 0;
	}
	addAnotherActivity(): void {
		orderModule.addAnotherActivity();
		this.$router.push(`/portal/${this.clientLocationKey}`);
	}
	cancelOrder(): void {
		if (!confirm('Cancel this order? All items will be removed.')) return;
		orderModule.abandonCheckout();
		this.$router.push(`/portal/${this.clientLocationKey}`);
	}
	handlePayment(): void {
		if (!this.activeOrder?.paymentDue && !this.activeOrder?.paid) {
			console.log('No Payment Generated!', this.activeOrder?.paymentDue, this.activeOrder?.paid);
			return;
		}
		if (this.activeOrder.paid) {
			this.activeOrder.savePayment({}).then(() => {
				console.log(this.activeOrder);
				if (!this.activeOrder?.isNew) {
					this.$router.push(`/portal/confirmation`);
				} else {
					console.log('Order is still new!');
				}
			});
			return;
		}
		this.localError = null;
		if (this.paymentJSON && this.activeOrder && this.activeOrder.paymentDue) {
			this.activeBox = BoxType.LOADING;
			this.activeOrder.paymentDue.PaymentMethodTypeKey == PaymentMethod.CARD;
			this.activeOrder?.savePayment({ Response: this.paymentJSON }).then(() => {
				console.log(this.activeOrder);
				if (!this.activeOrder?.isNew) {
					this.$router.push(`/portal/confirmation`);
				} else {
					this.activeBox = BoxType.PAYMENTS;
					this.localError = 'Retry failed, please contact a staff member for help';
					console.log('Order is still new!');
				}
			});
			return;
		}
		let wk = (window as any).webkit;
		(window as any).onPaymentCallback = (result: PaymentResult) => {
			if (result.success) {
				this.activeOrder?.savePayment({ Payment: result.squarePayment }).then(() => {
					console.log(this.activeOrder);
					if (!this.activeOrder?.isNew) {
						this.$router.push(`/portal/confirmation`);
					} else {
						this.activeBox = BoxType.PAYMENTS;
						this.localError = orderModule._activeOrder?.error ?? 'Unable to save booking, please try again';
						this.paymentJSON = JSON.stringify(result.squareJSON);
						console.log('Order is still new!');
					}
				});
			} else {
				this.activeBox = BoxType.PAYMENTS;
				this.localError = result.error ?? 'Unable to process card';
			}
		};
		this.activeBox = BoxType.LOADING;
		orderModule.openActivity?.checkTimes().then(async (valid: boolean) => {
			if (valid) {
				console.log('time is valid!');

				if (wk && wk.messageHandlers && wk.messageHandlers.onPayment) {
					// Square sync: create a Square order before taking payment if enabled and it's a quantity payment
					let squareOrderId: string | null = null;
					const location = clientModule.location;
					if (location?.EnableSquareSync === true && this.activeOrder?.paymentDue?.PaymentTypeKey === PaymentType.QUANTITY) {
						try {
							const sqRes = await apiService.post('CreateSquareOrderWithCompletedBlocker', {
								ClientLocationKey: location.ClientLocationKey,
								OrderKey: this.activeOrder?.orderKey,
								AmountCents: Math.round((this.activeOrder?.paymentDue?.Balance ?? 0) * 100),
								PasswordClientLoginKey: clientModule.clientPassword,
								PasscodeClientLoginKey: clientModule.clientPassword,
								Calculator: this.activeOrder?.paymentDue?.Calculator ?? null,
							});

							const hasSquareOrderId = sqRes?.SquareOrderId && sqRes.SquareOrderId.trim() !== '';

							if (hasSquareOrderId) {
								squareOrderId = sqRes.SquareOrderId;
							} else {
								// CreateSquareOrderResult has no Error field — failures arrive in
								// SqErrors/ModelErrors/ExceptionMessage. Without a SquareOrderId the dip would
								// proceed unattached to any Square order and lose itemization, so abort (same
								// outcome as the catch below for network failures).
								const errorStrings: string[] = [];
								if (sqRes?.ExceptionMessage) errorStrings.push(sqRes.ExceptionMessage);
								if (sqRes?.SqErrors?.length) sqRes.SqErrors.forEach((e: { Detail: string }) => errorStrings.push(e.Detail));
								if (sqRes?.ModelErrors?.length) sqRes.ModelErrors.forEach((e: { Value: string[] }) => errorStrings.push(e.Value[0]));
								this.activeBox = BoxType.PAYMENTS;
								this.localError = errorStrings.join(' ') || 'Failed to create Square order.';
								return;
							}
						} catch (e) {
							this.activeBox = BoxType.PAYMENTS;
							this.localError = 'Failed to create Square order.';
							return;
						}
					}

					const payload: Record<string, any> = {
						amount: this.activeOrder?.paymentDue?.Balance ?? 0,
						orderPaymentKey: this.activeOrder?.paymentDue?.OrderPaymentKey ?? null,
					};
					if (squareOrderId) payload.squareOrderId = squareOrderId;
					wk.messageHandlers.onPayment.postMessage(payload);
				}
			} else {
				this.activeBox = BoxType.PAYMENTS;
				this.localError = 'This time is no longer available, please go back and select another.';
			}
		});
	}
}
